import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiFetch } from '@jupi/ui'
import type { BriefingForLesson, BriefingPack, BriefingPackInput, BriefingPackSummary } from '@jupi/shared'

// Briefing packs (issue #268): read by students, written by instructors.

export interface SaveWarnings {
  unmatched: string[]
  unknown: { anchor: string; lessonNumber: string }[]
  duplicates: { lessonNumber: string; anchor: string }[]
}

export const IMAGE_BASE = (packId: string) => `/api/school/briefings/${packId}/images`

export function useBriefingPacks() {
  return useQuery({
    queryKey: ['school', 'briefings'],
    queryFn: () => api.get<{ packs: BriefingPackSummary[] }>('/api/school/briefings').then((r) => r.packs),
  })
}

export function useBriefingPack(packId: string | undefined) {
  return useQuery({
    queryKey: ['school', 'briefings', packId],
    queryFn: () => api.get<{ pack: BriefingPack }>(`/api/school/briefings/${packId}`).then((r) => r.pack),
    enabled: Boolean(packId),
  })
}

export function useLessonBriefings(lessonId: number | undefined) {
  return useQuery({
    queryKey: ['school', 'briefings', 'lesson', lessonId],
    queryFn: () =>
      api.get<{ packs: BriefingForLesson[] }>(`/api/school/briefings/by-lesson/${lessonId}`).then((r) => r.packs),
    enabled: lessonId !== undefined,
  })
}

export function useBriefingMutations() {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['school', 'briefings'] })
  const save = useMutation({
    mutationFn: async ({ packId, input }: { packId: string | null; input: BriefingPackInput }) =>
      packId
        ? api.put<{ packId: string; warnings: SaveWarnings }>(`/api/school/briefings/${packId}`, input)
        : api.post<{ packId: string; warnings: SaveWarnings }>('/api/school/briefings', input),
    onSuccess: refresh,
  })
  const remove = useMutation({
    mutationFn: (packId: string) => api.delete(`/api/school/briefings/${packId}`),
    onSuccess: refresh,
  })
  const uploadImage = useMutation({
    mutationFn: ({ packId, path, bytes }: { packId: string; path: string; bytes: Uint8Array }) => {
      const form = new FormData()
      form.append('path', path)
      form.append('file', new File([bytes as BlobPart], path.split('/').pop() ?? 'image'))
      return apiFetch<{ path: string }>(`/api/school/briefings/${packId}/images`, { method: 'POST', body: form })
    },
  })
  return { save, remove, uploadImage }
}
